/* eslint-disable @typescript-eslint/no-empty-function */
import {Button, IconButton} from "@zendeskgarden/react-buttons";
import {Dropdown, HeaderItem, Item, Menu, Trigger} from "@zendeskgarden/react-dropdowns";
import {Skeleton} from "@zendeskgarden/react-loaders";
import {TabPanel, Tabs} from '@zendeskgarden/react-tabs';
import {ReactComponent as VerticalKebabIcon} from '@zendeskgarden/svg-icons/src/16/overflow-vertical-fill.svg';
import cn from "classnames";
import {ReactElement, useContext, useEffect, useState} from "react";
import {toast} from "react-toastify";
import {ThemeProvider} from "styled-components";
import {
    EntityType,
    ForecastLocks,
    GetSinglePropertyDriversAndWorksheetItemsQuery,
    useGetFinancialValuesForYearLazyQuery,
    useGetForecastLocksLazyQuery,
    useGetNoteLazyQuery,
    useGetSinglePropertyDriversAndWorksheetItemsLazyQuery,
    useSetAccountLockMutation,
    useSetDriversMutation,
    VersionType
} from "../../../__generated__/generated_types";
import {
    buildEditableFxBarChecker,
    canOverrideAssumptionLock,
    checkAuthorization,
    TLockOverrides, userIsAdmin
} from "../../../authorization/AuthorizationCheckers";
import {Authorizations} from "../../../authorization/Authorizations";
import {BudgetingType} from "../../../BudgetingType";
import {AccountSummary} from "../../../components/account-summary/AccountSummary";
import AccountTable from "../../../components/account-table/AccountTable";
import {buildYearSummary} from "../../../components/account-table/logic/AccountTableHelpers";
import {AccountInstruction} from "../../../components/instruction/AccountInstruction";
import AccountNoteModal, {AccountNoteModalFetchInputs} from "../../../components/note/AccountNoteModal";
import AccountNoteViewButton from "../../../components/note/AccountNoteViewButton";
import StarredAccount from "../../../components/starred-account/StarredAccount";
import VZSaveStatus from "../../../components/vz-ui/vz-save-status/VZSaveStatus";
import * as workflowHeaderCSS from "../../../components/workflows/workflow-header/styles/css.module.scss";
import WorkflowHeader from "../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowNavToggle from "../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowPageLabel from "../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import WorkflowStepNav from "../../../components/workflows/workflow-step-nav/WorkflowStepNav";
import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET,
    WORKFLOW_YEAR_TAB_REFORECAST
} from "../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import {YearMonthRangeForProperty} from "../../../components/year-month-range/YearMonthRangeForProperty";
import {hydrateDriverAssumptions, TDriverMetrics} from "../../../contexts/account/data/logic/driversAndWorksheetData";
import {ISaveData} from "../../../contexts/account/data/useDriversAndWorksheetData";
import {getWithoutLocked} from "../../../contexts/account/data/useFormulaBarUpdates";
import {AuthContext} from "../../../contexts/AuthContext";
import {CategoryDescriptor, Account as CoaAccount} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {Property} from "../../../contexts/properties/logic";
import {useConfig} from "../../../hooks/useConfig";
import * as workflowCSS from "../../../styles/workflows/workflowCSS.module.scss";
import {ViziblyTheme} from "../../../styles/zendesk-garden/ViziblyZDGTheme";
import {formatterGlName} from "../../../utils/formatters";
import {IWorkflowPageProps} from "../logic/workflows";
import "./Account.scss";
import {AccountSummaryContextProvider, IAccountSummaryData} from "./AccountSummaryContext";
import {AccountAuditLogSidebar} from "./components/AccountAuditLogSidebar";
import FormulaBar from "./formula-bar/FormulaBar";
import LockBanner from "./LockBanner";
import * as css from "./styles/styles.module.scss";


export interface IAccountProps extends IWorkflowPageProps {
   accountId: string;
   type: string;
   budgetYear: number;
}

function Account({onPreviousClick, onNextClick, inUserWorkflow, accountId, type, budgetYear}: IAccountProps): ReactElement {
    const config = useConfig();
    const currentProperty = config.properties.currentProperty;
    const {user} = useContext(AuthContext);
    const [account, setAccount] = useState<CoaAccount | undefined>(undefined);
    const [parentCategories, setParentCategories] = useState<CategoryDescriptor[]>([]);
    const [selectedTab, setSelectedTab] = useState(WORKFLOW_YEAR_TAB_REFORECAST);
    const year = BudgetingType.REFORECAST == type ? config.year : (config.year + 1);
    const versionType = BudgetingType.REFORECAST == type ? VersionType.Reforecast : VersionType.Budget;
    const [parsedGraphFinancialData, setParsedGraphFinancialData] = useState<IAccountSummaryData>({years: {}});
    const [combinedData, setCombinedData] = useState<{
        driversAndWorksheetData: GetSinglePropertyDriversAndWorksheetItemsQuery,
        financialYearValues: IAccountSummaryData,
    }>();
    const [forecastLocks, setForecastLocks] = useState<ForecastLocks | undefined>();
    const [lockOverrides, setLockOverrides] = useState<TLockOverrides | undefined>();
    const [lockValuesForMenu, setLockValuesForMenu] = useState<string[]>([]);
    // used to set the currently active account note + driver notes being viewed/edited
    const [accountNoteModalInputs, setAccountNoteModalInputs] = useState<AccountNoteModalFetchInputs | null>(null);
    const [lastSaved, setLastSaved] = useState<Date>(new Date());
    const [isTableBusySaving, setIsTableBusySaving] = useState(false);

    const [getFinancialValues, {data: financialValues, loading: financialValuesLoading}] = useGetFinancialValuesForYearLazyQuery({
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "no-cache",
    });
    const [getDriverAndWorksheetData, {data: rawData,loading: dataLoading}] = useGetSinglePropertyDriversAndWorksheetItemsLazyQuery({
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "no-cache",
    });
    const [setDrivers, {data: setDriversData, loading: setDriversLoading}] = useSetDriversMutation();
    const [getForecastLocks, {data: dataForecastLocks, loading: forecastLocksLoading}] = useGetForecastLocksLazyQuery({
        fetchPolicy: "no-cache",
    });
    const [setAccountLock, {loading: accountLockSaving}] = useSetAccountLockMutation({
        onCompleted: () => {
            toast.success("Lock Updated");
        }
    });
    const [fetchNote, {data: noteData, refetch: refetchNote}] = useGetNoteLazyQuery({fetchPolicy: "network-only"});

    const [auditLogOpen, setAuditLogOpen] = useState<boolean>(false);

    const saveData = (args: ISaveData): void => {
        setDrivers({
            variables: {
                accountId: args.accountId,
                propertyId: args.propertyId,
                year: args.year,
                versionType: args.versionType,
                isUserRequestingWorksheetDeletion: args.isUserRequestingWorksheetDeletion,
                pendingUpdates: getWithoutLocked(args.pendingUpdates),
            },
        });
    };

    useEffect(() => {
        if (!user || !dataForecastLocks || !dataForecastLocks.forecastLocks || forecastLocksLoading) {
            return;
        }

        let reforecastLocked = false;
        let budgetLocked = false;
        let lockOverrides;
        const lockStrings = [];

        if (dataForecastLocks.forecastLocks.account?.reforecastLocked) {
            reforecastLocked = true;
            lockStrings.push("reforecastLocked");
        }

        if (dataForecastLocks.forecastLocks.account?.budgetLocked) {
            budgetLocked = true;
            lockStrings.push("budgetLocked");
        }

        if (dataForecastLocks.forecastLocks.property?.reforecastLocked) {
            reforecastLocked = true;
        }

        if (dataForecastLocks.forecastLocks.property?.budgetLocked) {
            budgetLocked = true;
        }

        if (reforecastLocked || budgetLocked) {
            lockOverrides = canOverrideAssumptionLock(user, dataForecastLocks.forecastLocks);
            setLockOverrides(lockOverrides);
        } else {
            setLockOverrides(undefined);
        }

        setForecastLocks({
            reforecastLocked,
            budgetLocked,
        });

        setLockValuesForMenu(lockStrings);

    }, [dataForecastLocks, dataForecastLocks?.forecastLocks, user, forecastLocksLoading]);

    const [parsedData, setParsedData] = useState<TDriverMetrics>();
    useEffect(
        () => {
            if(dataLoading || rawData === undefined){
                return;
            }

            setParsedData( prevState => {
                return {
                    ...prevState ?? {},
                    ...hydrateDriverAssumptions(rawData),
                };
            });

        },
        [dataLoading, rawData]
    );

    function fetchItAll() {
        if (!config.properties.currentProperty) {
            return;
        }

        let years;
        if (versionType == VersionType.Reforecast) {
            years = [
                {year: year, versionType: VersionType.Budget},
                {year: year, versionType: VersionType.Reforecast},
                {year: year, versionType: VersionType.Actuals},
                {year: year - 1, versionType: VersionType.Actuals},
                {year: year - 2, versionType: VersionType.Actuals},
                {year: year - 3, versionType: VersionType.Actuals},
                {year: year - 4, versionType: VersionType.Actuals},
            ];
        } else {
            years = [
                {year: year, versionType: VersionType.Budget},
                {year: year - 1, versionType: VersionType.Reforecast},
                {year: year - 1, versionType: VersionType.Actuals},
                {year: year - 2, versionType: VersionType.Actuals},
                {year: year - 3, versionType: VersionType.Actuals},
                {year: year - 4, versionType: VersionType.Actuals},
                {year: year - 5, versionType: VersionType.Actuals},
            ];
        }

        getDriverAndWorksheetData({variables: {
            propertyId: config.properties?.currentProperty?.id,
            accountId: accountId,
            year: year,
            versionType: versionType,
            budgetYear: budgetYear
        }});
        getFinancialValues({
            variables: {
                propertyIds: [config.properties.currentProperty.id],
                entityIds: accountId,
                years,
                budgetYear
            }
        });
        fetchNote({
            variables: {
                propertyId: config.properties.currentProperty.id,
                entityId: accountId,
                entityType: EntityType.Account,
                year: versionType === VersionType.Reforecast ? budgetYear - 1 : budgetYear,
                versionType: versionType,
            }
        })
    }

    /**
     * Re-fetch drivers after setDrivers() executes successfully
     */
    useEffect(() => {
        if (setDriversData === undefined || setDriversData === null) {
            return;
        }

        fetchItAll();
    }, [setDriversData]);

    function updateGraphs(newFinancialData: IAccountSummaryData): void {
        const newData: IAccountSummaryData = {
            years: {
                ...parsedGraphFinancialData.years,
                ...newFinancialData.years,
            }
        };
        setParsedGraphFinancialData(newData);
    }

    useEffect(
        () => {
            if (!config.isReady || accountId === undefined || config.properties.currentProperty === undefined) {
                return;
            }

            getForecastLocks({
                variables: {
                    propertyId: config.properties.currentProperty.id,
                    budgetYear: budgetYear,
                    accountId: accountId,
                }
            });

            if (type) {
                config.versions.setSelectedVersionType(type == BudgetingType.REFORECAST ? VersionType.Reforecast : VersionType.Budget);
            }
        },
        [config.isReady, accountId, type, config.properties]
    );

    useEffect(
        () => {
            if (!config.isReady || !accountId || !config.properties.currentProperty || !versionType) {
                return;
            }

            fetchItAll();
        },
        [config.isReady, config.properties.currentProperty, accountId, versionType]
    );

    useEffect(() => {
        if (dataLoading || financialValuesLoading || !rawData || !financialValues) {
            return;
        }

        const ctxData = buildYearSummary(financialValues.financialValuesForYear, config.startReforecastMonthIndex);
        setParsedGraphFinancialData(ctxData);
        setCombinedData({driversAndWorksheetData: rawData, financialYearValues: ctxData});
    }, [financialValuesLoading, dataLoading, rawData, financialValues]);

    /**
     * Update selected tab, which updates display, on type change
     */
    useEffect(
        () => {
            if (type == BudgetingType.REFORECAST && selectedTab != WORKFLOW_YEAR_TAB_REFORECAST) {
                setSelectedTab(WORKFLOW_YEAR_TAB_REFORECAST);
                config.versions.setSelectedVersionType(VersionType.Reforecast);
            }
            if (type == BudgetingType.BUDGET && selectedTab != WORKFLOW_YEAR_TAB_BUDGET) {
                setSelectedTab(WORKFLOW_YEAR_TAB_BUDGET);
                config.versions.setSelectedVersionType(VersionType.Budget);
            }
        },
        [type]
    );

    useEffect(() => {
        if (config.isReady) {
            const account = config.chartOfAccountsConfig?.accounts?.find(acc => acc.id == accountId);
            const parentCategories = config.chartOfAccountsConfig.getParentCategories(account?.parentId ?? "");
            setAccount(account);
            setParentCategories(parentCategories);
        }
    }, [config.isReady, config.chartOfAccountsConfig, config.properties?.currentProperty?.id, accountId]);

    const isReforecast = type === BudgetingType.REFORECAST;
    const modelVersions = config.versions.getModelVersions(config.year);
    const versionId = isReforecast ? modelVersions.reforecastVersionId : modelVersions.nextYearBudgetVersionId;

    /**
     * Fx bar is readonly regardless or roles and permissions when either:
     * 1. The EditableFormulaBar feature is turned OFF, or
     * 2. The current forecast period is locked for the whole property
     */
    const isFxBarReadOnly = (versionType == VersionType.Reforecast && (dataForecastLocks?.forecastLocks?.property.reforecastLocked === true))
        || (versionType == VersionType.Budget && (dataForecastLocks?.forecastLocks?.property.budgetLocked === true));

    const accountLockedForVersionType = versionType === VersionType.Reforecast
        ? dataForecastLocks?.forecastLocks?.account?.reforecastLocked
        : dataForecastLocks?.forecastLocks?.account?.budgetLocked;

    function togglePeriodLock(period: string | null) {
        if (period == null || !config.properties.currentProperty || accountLockSaving) {
            return;
        }

        const propertyId = config.properties.currentProperty.id;

        let locked = false;

        if (period == "reforecastLocked") {
            locked = !lockValuesForMenu.includes("reforecastLocked");
        } else if (period == "budgetLocked") {
            locked = !lockValuesForMenu.includes("budgetLocked");
        }

        setAccountLock({
            variables: {
                propertyId,
                accountId,
                budgetYear,
                versionType: period == "reforecastLocked" ? VersionType.Reforecast : VersionType.Budget,
                locked,
            }
        }).then(() => {
            getForecastLocks({
                variables: {
                    propertyId,
                    budgetYear,
                    accountId,
                }
            });
        });
    }

    function showLockBanner(): boolean {
        if (!dataForecastLocks?.forecastLocks) {
            return false;
        }

        if (versionType == VersionType.Reforecast) {
            return (dataForecastLocks?.forecastLocks.account?.reforecastLocked && !lockOverrides?.reforecast && !dataForecastLocks?.forecastLocks.property.reforecastLocked) ?? false;
        } else if (versionType == VersionType.Budget) {
            return (dataForecastLocks?.forecastLocks.account?.budgetLocked && !lockOverrides?.budget && !dataForecastLocks?.forecastLocks.property.budgetLocked) ?? false;
        } else {
            return false;
        }
    }

    function getLockBannerText(property: Property | undefined, versionType: VersionType): string {
        let partial = `${year}`;
        if(property && property.isAcquisitionProperty) {
            if(versionType == VersionType.Reforecast) {
                partial = "Year 1";
            } else {
                partial = "Year 2";
            }
        }

        return `This GL account has been locked for ${partial}. No edits are permitted`;
    }

    function getLockMenuText(property: Property, versionType: VersionType): string {
        let partial = "";
        if(property.isAcquisitionProperty) {
            partial = "Year 1";
            if(versionType == VersionType.Budget) {
                partial = "Year 2";
            }
        } else {
            partial = `${property.reforecastYear} Reforecast`;
            if(versionType == VersionType.Budget) {
                partial = `${property.budgetYear} Budget`;
            }
        }

        return `Lock GL for ${partial}`;
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <div className={workflowCSS.workflowContainer}>
                <div className={workflowCSS.workflowContainerInner}>
                    <WorkflowHeader>
                        <WorkflowHeader.LeftCol>
                            <div className={workflowHeaderCSS.rowItemsContainer}>
                                <WorkflowNavToggle inUserWorkflow={inUserWorkflow} />
                                <span className={workflowCSS.starredAccount}>
                                    <StarredAccount
                                        accountId={accountId}
                                        versionIds={[modelVersions.reforecastVersionId, modelVersions.nextYearBudgetVersionId]}
                                        currentVersionId={versionId}
                                    />
                                </span>
                                <WorkflowPageLabel label={account ? formatterGlName(account.name, account.accountNumber) : 'Account'}/>
                                {checkAuthorization(user, Authorizations.EditLockUnlockAccounts) && currentProperty &&
                                    <Dropdown
                                        selectedItems={lockValuesForMenu}
                                        onSelect={(_selections, itemClicked) => togglePeriodLock(itemClicked.inputValue)}
                                    >
                                        <Trigger>
                                            <IconButton aria-label="plant">
                                                <VerticalKebabIcon />
                                            </IconButton>
                                        </Trigger>
                                        <Menu className={css.lockMenu}>
                                            <HeaderItem className={css.lockMenuHeaderText}>
                                                GL ACCOUNT LOCKING OPTIONS
                                            </HeaderItem>
                                            <Item
                                                value="reforecastLocked"
                                            >
                                                {getLockMenuText(currentProperty, VersionType.Reforecast)}
                                            </Item>
                                            {!currentProperty.isAcquisitionProperty &&
                                                <Item
                                                    value="budgetLocked"
                                                >
                                                    {getLockMenuText(currentProperty, VersionType.Budget)}
                                                </Item>
                                            }
                                        </Menu>
                                    </Dropdown>
                                }
                            </div>
                        </WorkflowHeader.LeftCol>
                        <WorkflowHeader.RightCol>
                                <VZSaveStatus isSaving={isTableBusySaving || setDriversLoading} lastSaved={lastSaved} />
                                {userIsAdmin(user) &&
                                    <div>
                                        <Button isPrimary type="button" onClick={() => setAuditLogOpen(true)}>Audit Log</Button>
                                    </div>
                                }
                                <WorkflowStepNav onPreviousClick={onPreviousClick} onNextClick={onNextClick} inUserWorkflow={inUserWorkflow} />
                        </WorkflowHeader.RightCol>
                    </WorkflowHeader>
                    <div className={workflowCSS.bodyContainer}>
                        <AccountSummaryContextProvider>
                            <Tabs selectedItem={selectedTab} className={workflowCSS.tabsContainer}>
                                {currentProperty &&
                                    <WorkflowYearTabs
                                        property={currentProperty}
                                        selectedTab={selectedTab}
                                        rawForecastLocks={dataForecastLocks}
                                        forecastLocks={forecastLocks}
                                        lockOverrides={lockOverrides}
                                    />
                                }
                                {showLockBanner() &&
                                    <LockBanner text={getLockBannerText(currentProperty, versionType)} />
                                }
                                <AccountNoteModal
                                    accountName={account?.name ?? ""}
                                    noteFetchInputs={accountNoteModalInputs}
                                    onSave={() => setAccountNoteModalInputs(null)}
                                    onClose={() => setAccountNoteModalInputs(null)}
                                />
                                <TabPanel item={WORKFLOW_YEAR_TAB_REFORECAST} className={workflowCSS.tabContent}>
                                    <div className={workflowCSS.dataColumn}>
                                        {
                                            user && config.isReady && (accountLockedForVersionType !== undefined)
                                                ? <div className={css.accountFormulaBar}>
                                                    {currentProperty &&
                                                        <YearMonthRangeForProperty property={currentProperty} versionType={VersionType.Reforecast} className={css.formulaDateRange} />
                                                    }
                                                    <FormulaBar
                                                        budgetYear={budgetYear}
                                                        year={config.year}
                                                        versionType={VersionType.Reforecast}
                                                        currentPropertyId={config.properties.currentProperty?.id}
                                                        accountId={accountId}
                                                        readOnly={isFxBarReadOnly}
                                                        driversAndWorksheetData={{
                                                            dataLoading,
                                                            parsedData,
                                                            rawData,
                                                            saveData,
                                                        }}
                                                        editableFxBarChecker={buildEditableFxBarChecker(user, accountLockedForVersionType)}
                                                        warnOnUnsavedChanges
                                                    /></div>
                                                : <></>
                                        }
                                        {
                                            config.isReady && config.properties.currentProperty && account && combinedData ?
                                            <AccountTable
                                                user={user}
                                                key={account.id + config.properties.currentProperty.id}
                                                year={year}
                                                versionType={VersionType.Reforecast}
                                                versionId={versionId}
                                                property={config.properties.currentProperty}
                                                account={account}
                                                combinedData={combinedData}
                                                forecastLocks={forecastLocks}
                                                lockOverrides={lockOverrides}
                                                forceDataRefetch={fetchItAll}
                                                dataUpdated={updatedData => {
                                                    updateGraphs(updatedData);
                                                    setLastSaved(new Date());
                                                }}
                                                isDataSaving={setIsTableBusySaving}
                                                budgetYear={budgetYear}
                                            />
                                            : <div className={workflowCSS.tableSkeletonLoader}>
                                                    <Skeleton height={"220px"} width={"100%"} />
                                                    <table>
                                                        <tbody>
                                                            {Array(4).fill(null).map((_, index) =>
                                                                <tr key={index}>
                                                                    {Array(14).fill(null).map((_, indexTd) =>
                                                                        <td key={indexTd} />
                                                                    )}
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                        }
                                    </div>
                                    <div className={workflowCSS.actionsCol}>
                                        <AccountInstruction
                                            accountId={accountId}
                                            versionId={versionId}
                                        />
                                        {account && currentProperty &&
                                            <AccountNoteViewButton
                                                notePreview={noteData?.queryNote?.text}
                                                iconOnly
                                                onActivate={() => setAccountNoteModalInputs({
                                                    propertyId: currentProperty?.id,
                                                    accountId: account.id,
                                                    budgetYear: budgetYear,
                                                    versionType: VersionType.Reforecast,
                                                })}
                                            />
                                        }
                                    </div>
                                </TabPanel>
                                <TabPanel item={WORKFLOW_YEAR_TAB_BUDGET} className={workflowCSS.tabContent}>
                                    <div className={workflowCSS.dataColumn}>
                                        <div className={cn(workflowCSS.accountTableContainer, workflowCSS.accountSummaryTable)}>
                                            {
                                                user && config.isReady && (accountLockedForVersionType !== undefined)
                                                    ? <div className={css.accountFormulaBar}>
                                                        {currentProperty &&
                                                            <YearMonthRangeForProperty property={currentProperty} versionType={VersionType.Budget} className={css.formulaDateRange} />
                                                        }
                                                        <FormulaBar
                                                            year={config.year}
                                                            budgetYear={budgetYear}
                                                            versionType={VersionType.Budget}
                                                            currentPropertyId={config.properties.currentProperty?.id}
                                                            accountId={accountId}
                                                            readOnly={isFxBarReadOnly}
                                                            driversAndWorksheetData={{
                                                                dataLoading: (dataLoading || setDriversLoading),
                                                                parsedData,
                                                                rawData,
                                                                saveData,
                                                            }}
                                                            editableFxBarChecker={buildEditableFxBarChecker(user, accountLockedForVersionType)}
                                                            warnOnUnsavedChanges
                                                        /></div>
                                                    : <></>
                                            }
                                            {
                                                config.isReady && config.properties.currentProperty && account && combinedData ?
                                                    <AccountTable
                                                        user={user}
                                                        key={account.id + config.properties.currentProperty.id}
                                                        year={year}
                                                        versionType={VersionType.Budget}
                                                        versionId={versionId}
                                                        property={config.properties.currentProperty}
                                                        account={account}
                                                        combinedData={combinedData}
                                                        forecastLocks={forecastLocks}
                                                        lockOverrides={lockOverrides}
                                                        forceDataRefetch={fetchItAll}
                                                        dataUpdated={updatedData => {
                                                            updateGraphs(updatedData);
                                                            setLastSaved(new Date());
                                                        }}
                                                        isDataSaving={setIsTableBusySaving}
                                                        budgetYear={budgetYear}
                                                    />
                                                    : <div className={workflowCSS.tableSkeletonLoader}>
                                                        <Skeleton height={"220px"} width={"100%"} />
                                                        <table>
                                                            <tbody>
                                                                {Array(4).fill(null).map((_, index) =>
                                                                    <tr key={index}>
                                                                        {Array(14).fill(null).map((_, indexTd) =>
                                                                            <td key={indexTd} />
                                                                        )}
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                            }
                                        </div>
                                    </div>
                                    <div className={workflowCSS.actionsCol}>
                                        <AccountInstruction
                                            accountId={accountId}
                                            versionId={versionId}
                                        />
                                        {account && currentProperty &&
                                            <AccountNoteViewButton
                                                notePreview={noteData?.queryNote?.text}
                                                iconOnly
                                                onActivate={() => setAccountNoteModalInputs({
                                                    propertyId: currentProperty?.id,
                                                    accountId: account.id,
                                                    budgetYear: budgetYear,
                                                    versionType: VersionType.Budget,
                                                })}
                                            />
                                        }
                                    </div>
                                </TabPanel>
                            </Tabs>
                            {
                                config.isReady && config.chartOfAccountsConfig.isReady && accountId && parsedGraphFinancialData && currentProperty &&
                                <AccountSummary
                                    chartOfAccounts={config.chartOfAccountsConfig}
                                    accountId={accountId}
                                    parentCategories={parentCategories}
                                    configYear={config.year}
                                    rfcstStartMonth={config.startReforecastMonthIndex}
                                    financialYearValues={parsedGraphFinancialData}
                                    versionType={versionType}
                                    property={currentProperty}
                                    budgetYear={budgetYear}
                                />
                            }
                        </AccountSummaryContextProvider>
                    </div>
                </div>
            </div>
            {currentProperty && auditLogOpen &&
                <AccountAuditLogSidebar isOpen={auditLogOpen} onClose={() => setAuditLogOpen(false)} accountId={accountId} propertyId={currentProperty.id} />
            }
        </ThemeProvider>
    );
}

// TODO: Determine if memoization is necessary. Moved memo wrapper here from AccountRoute.
// export default memo(Account);

export default Account;
